<template>
  <div>
    <nav class="toolbox toolbox-pagination" v-if="showPagination">
      <div class="toolbox-item toolbox-show">
        <label>{{ paginationResultsLocalTitle }}</label>
      </div>
      <!-- End .toolbox-item -->
      <ul class="pagination">
        <li
          class="page-item"
          :class="{ disabled: prevDisabled }"
          @click.prevent.stop="goToPage(1)"
        >
          <span class="page-link page-link-btn">
            <i class="icon-angle-double-left" />
          </span>
        </li>
        <li
          class="page-item"
          :class="{ disabled: prevDisabled }"
          @click.prevent.stop="handlePreviousPage"
        >
          <span class="page-link page-link-btn"><i class="icon-angle-left"></i></span>
        </li>
        <li
          class="page-item"
          :class="{ active: index === normalizedPager.currentPage }"
          v-for="index in pages"
          :key="index"
          @click.stop.prevent="goToPage(index)"
        >
          <span class="page-link">{{ index }}</span>
        </li>
        <li
          class="page-item"
          :class="{ disabled: nextDisabled }"
          @click.prevent.stop="handleNextPage"
        >
          <span class="page-link page-link-btn"><i class="icon-angle-right"></i></span>
        </li>

        <li
          class="page-item"
          :class="{ disabled: nextDisabled }"
          @click.prevent.stop="goToPage(normalizedPager.totalPages)"
        >
          <span class="page-link page-link-btn">
            <i class="icon-angle-double-right" />
          </span>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import { translations } from '@/mixins';

const defaultPager = {
  currentPage: 1,
  totalItems: 0,
  totalPages: 1,
};

export default {
  name: 'LocalPagination',
  mixins: [translations],
  props: {
    pager: {
      type: Object,
      default: () => ({ ...defaultPager }),
    },
    paginationProps: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    normalizedPager() {
      const pager = this.pager || {};

      return {
        currentPage: Number.isFinite(pager.currentPage) && pager.currentPage > 0
          ? pager.currentPage
          : defaultPager.currentPage,
        totalItems: Number.isFinite(pager.totalItems) && pager.totalItems >= 0
          ? pager.totalItems
          : defaultPager.totalItems,
        totalPages: Number.isFinite(pager.totalPages) && pager.totalPages > 0
          ? pager.totalPages
          : defaultPager.totalPages,
      };
    },
    pageSize() {
      return this.paginationProps.pageSize || 12;
    },
    pages() {
      const { currentPage, totalPages } = this.normalizedPager;
      const numberOfPages = 3;
      const min = currentPage - numberOfPages >= 1 ? currentPage - numberOfPages : 1;
      const max = currentPage + numberOfPages <= totalPages
        ? currentPage + numberOfPages
        : totalPages;

      return Array.from({ length: Math.max(max - min + 1, 0) }, (_, index) => min + index);
    },
    toResults() {
      const { totalItems, currentPage } = this.normalizedPager;
      return totalItems < currentPage * this.pageSize
        ? totalItems
        : currentPage * this.pageSize;
    },
    fromResults() {
      const { currentPage } = this.normalizedPager;
      return currentPage === 1 ? 1 : (currentPage - 1) * this.pageSize + 1;
    },
    nextDisabled() {
      return this.normalizedPager.currentPage === this.normalizedPager.totalPages;
    },
    prevDisabled() {
      return this.normalizedPager.currentPage === 1;
    },
    showPagination() {
      return this.pages.length > 1;
    },
    paginationResultsLocalTitle() {
      return this.translations.pagingResults.replace('{fromResults}', this.fromResults)
        .replace('{toResults}', this.toResults)
        .replace('{totalItems}', this.normalizedPager.totalItems);
    },
  },
  methods: {
    goToPage(page) {
      if (page === this.normalizedPager.currentPage) return;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.$emit('page-change', page);
    },
    handlePreviousPage() {
      if (this.prevDisabled) return;
      this.goToPage(this.normalizedPager.currentPage - 1);
    },
    handleNextPage() {
      if (this.nextDisabled) return;
      this.goToPage(this.normalizedPager.currentPage + 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.toolbox.toolbox-pagination {
  row-gap: 1rem;
}

.toolbox.toolbox-pagination .pagination {
  flex-wrap: wrap;
}

.page-item:not(.disabled) {
  cursor: pointer;
}

.page-item.disabled {
  cursor: not-allowed;
}

.page-link.page-link-btn {
  border: 0.1rem solid #e9e9e9;
  min-width: 3.2rem;
  font-size: 1.4rem;
}

@media (max-width: 991px) {
  .toolbox.toolbox-pagination {
    justify-content: center;
    margin-bottom: 3rem;
    padding-top: 1.4rem;
  }

  .toolbox.toolbox-pagination .pagination {
    justify-content: center;
    margin-left: 0;
  }
}
</style>
